import { J2Multiply } from '@e3dc-react/icons'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, IconButton, Typography as MuiTypography } from '@mui/material'
import React from 'react'
import { MobileDrawer } from 'src/Elements/Drawer'
import { makeStyles } from 'tss-react/mui'
import { APP_FEEDBACK_EMAIL_HREF } from '../consts'
import { getThemedBlankViewImage, isFlowTheme } from 'src/Libs/utils'
import { BlankViewImageVariants } from 'src/Components/blank-view'

const BLANK_VIEW_IMAGE_WIDTH = 84
const BLANK_VIEW_IMAGE_HEIGHT = 87

const useStyles = makeStyles()(theme => ({
  container: {
    top: `calc(100% - 330px)`,
  },
  exitButton: {
    height: 30,
    width: 30,
    color: theme.palette.grey.grey00,
  },
  rateButton: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.shades.shade04,
    border: `${theme.palette.shades.shade03} solid 1px`,
    height: 180,
    width: 'auto',
    display: 'flex',
    justifyContent: 'center',
    flexGrow: 1,
    alignItems: 'center',
    color: theme.palette.primary.main,
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}))

export interface Props {
  handleRemindMeLater: () => void
  handleSendEmail: () => void
  handleRateApp: () => void
  handleClose: () => void
  handleDislikeApp: () => void
  open: boolean
  emailFeedbackContentActive: boolean
}

/**
 * A modal component for the app rating
 */
const AppRating: React.FC<Props> = ({
  handleRemindMeLater,
  handleSendEmail,
  handleRateApp,
  handleClose,
  handleDislikeApp,
  open,
  emailFeedbackContentActive,
}) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  const blankViewImageWorried = getThemedBlankViewImage(BlankViewImageVariants.Worried)
  const blankViewImageHappy = getThemedBlankViewImage(BlankViewImageVariants.Happy)
  const feedbackText = isFlowTheme ? t('rate-app.flow-email-feedback-text') : t('rate-app.email-feedback-text')
  return (
    <MobileDrawer anchor="bottom" onClose={handleClose} open={open} closableOnlyWithActionButtons containerStyle={classes.container}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: 30 }} />
        <MuiTypography variant="h2" sx={{ flex: 1 }} textAlign={'center'}>
          {emailFeedbackContentActive ? t('rate-app.email-feedback-title') : t('rate-app.title')}
        </MuiTypography>
        <IconButton onClick={handleClose} className={classes.exitButton}>
          <J2Multiply />
        </IconButton>
      </Box>

      {emailFeedbackContentActive ? (
        <Box sx={{ pt: 4, display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 2 }}>
          <Typography
            variant="body1"
            align="center"
            components={{
              a: <Box component={'a'} href={APP_FEEDBACK_EMAIL_HREF} sx={{ textDecoration: 'none', color: 'primary.main' }} />,
            }}
          >
            {feedbackText}
          </Typography>
          <Button variant="contained" onClick={handleSendEmail}>
            {t('rate-app.feedback-button-text')}
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, pt: 4, color: 'primary.main' }}>
          <Box className={classes.rateButton} onClick={handleDislikeApp}>
            <Box sx={{ width: BLANK_VIEW_IMAGE_WIDTH, height: BLANK_VIEW_IMAGE_HEIGHT }} component="img" src={blankViewImageWorried} />
            <MuiTypography variant="h3">{t('common.no')}</MuiTypography>
          </Box>
          <Box className={classes.rateButton} onClick={handleRateApp}>
            <Box sx={{ width: BLANK_VIEW_IMAGE_WIDTH, height: BLANK_VIEW_IMAGE_HEIGHT }} component="img" src={blankViewImageHappy} />
            <MuiTypography variant="h3">{t('common.yes')}</MuiTypography>
          </Box>
        </Box>
      )}
      {!emailFeedbackContentActive && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, mx: 1, my: '10px' }} onClick={handleRemindMeLater}>
          <MuiTypography variant="overline" color={'primary'}>
            {t('rate-app.remind-me-later')}
          </MuiTypography>
        </Box>
      )}
    </MobileDrawer>
  )
}

export default AppRating
